/**
 * Functions for firing events.
 * @module app.shared
 */

const DEFAULTS: CustomEventInit = Object.freeze({ bubbles: true, cancelable: true });

/**
 * @internal
 * Fires an event on an element.
 */
function fire(element: EventTarget, event: string, init: CustomEventInit): boolean {
    return element.dispatchEvent(new CustomEvent(event, init));
}

/**
 * @internal
 * Transforms a "CustomEventInit or detail property" into a normalized CustomEventInit.
 */
function normalize(init?: CustomEventInit | any): CustomEventInit {
    if(init === undefined || init === null) {
        return { ...DEFAULTS };
    } else if(!(typeof init === 'object') || !Reflect.has(init, 'detail')) {
        return { ...DEFAULTS, detail: init };
    } else {
        return { ...DEFAULTS, ...init };
    }
}

/**
 * @internal
 * Gets element bubble targets for one or more selectors.
 * @param {Element} element The bubble target.
 * @param {string | string[]} selectors One or more selectors to match bubbled elements.
 * @returns {EventTarget[]} Bubbled elements that match the given selectors.
 */
function bubble(element: Element, selectors: string | string[]): EventTarget[];
function bubble(element: Element | null, selectors: string | string[]): EventTarget[] {
    if(!Array.isArray(selectors)) {
        selectors = [selectors];
    }
    const matches = [];
    while(element !== null) {
        if(selectors.some((selector) => element!.matches(selector))) {
            matches.push(element);
        }
        element = element.parentElement;
    }
    return matches;
}


/**
 * Fires a custom event on an element.
 *
 * @param {EventTarget} target - The target element for the event.
 * @param {string} event - The event type name.
 * @param {CustomEventInit | any} [init={ bubbles: true, cancelable: true }] - The event options, or the detail value for the event options.
 * @return {boolean} `false` if the event was cancelled.
 */
export function fireEvent(element: EventTarget, event: string, init?: CustomEventInit | any): boolean {
    return fire(element, event, normalize(init));
}

/**
 * Fires one or more custom events on an element.
 * @param {EventTarget} target - The target element for the event.
 * @param {string|string[]} event - The event type name or names.
 * @param {CustomEventInit | any} [init={ bubbles: true, cancelable: true }] - The event options, or the detail value for the event options.
 * @return {boolean[]} `false` if the event was cancelled for each event type name.
 */
export function fireEvents(element: EventTarget, events: string | string[], init?: CustomEventInit | any): boolean[] {
    if(!Array.isArray(events)) {
        events = [events];
    }
    init = normalize(init);
    return events.map((event) => fire(element, event, init));
}

/**
 * Fires a non-bubbling custom event on ancestors matching a given selector.
 * @note Events fired by this method will never bubble.
 * @param {EventTarget} target - The target element for the event.
 * @param {string} event - The event type name.
 * @param {CustomEventInit | any} [init={ bubbles: false, cancelable: true }] - The event options, or the detail value for the event options.
 * @return {boolean} `false` if the event was cancelled
 */
export function fireEventOn(target: EventTarget, event: string, init?: CustomEventInit | any): boolean {
    init = Object.assign(normalize(init), { bubbles: false });
    return fire(target, event, init);
}

/**
 * Fires a non-bubbling custom event on ancestors matching a given selector.
 * @note Events fired by this method will never bubble.
 * @param {EventTarget} target - The target element for the event.
 * @param {string|string[]} event - The event type name or names.
 * @param {CustomEventInit | any} [init={ bubbles: false, cancelable: true }] - The event options, or the detail value for the event options.
 * @return {boolean[]} `false` if the event was cancelled for each event type name.
 */
export function fireEventsOn(target: EventTarget, events: string | string[], init?: CustomEventInit | any): boolean[] {
    if(!Array.isArray(events)) {
        events = [events];
    }
    return events.map((event) => fireEventOn(target, event, init));
}

export function deferEvent(element: EventTarget, event: Event) {
    event.stopPropagation();
    Promise.resolve().then(() => element.dispatchEvent(event));
}
